const APOLLO_APP_ID = '68bc69348f69ec0019b37928';
const LINKEDIN_PARTNER_ID = '7522148';
const TIKTOK_PIXEL_ID = 'D9PSOTRC77U7RKPO91PG';
const TIKTOK_CHECKOUT_KEY = 'finnix_tiktok_checkout';

const hasWindow = () => typeof window !== 'undefined' && typeof document !== 'undefined';

// Marketing scripts never run on financial, demo-upload or authentication pages.
export const isMarketingPage = (path = window.location.pathname) => (
  ['/', '/about', '/select-plan', '/pricing', '/cookie-policy', '/privacy', '/terms'].includes(path)
  || ['/product/', '/solutions/', '/use-cases/'].some(prefix => path.startsWith(prefix))
);

const hasMarketingConsent = () => {
  if (!hasWindow() || !isMarketingPage()) return false;
  try {
    const saved = window.localStorage.getItem('cookie_consent');
    return saved ? Boolean(JSON.parse(saved).marketing) : false;
  } catch (_error) {
    return false;
  }
};

const getCurrentPath = () => window.location.pathname;

const getStorage = (type) => {
  if (!hasWindow()) return null;
  try {
    return window[type];
  } catch (_error) {
    return null;
  }
};

export const initializeTikTokPixel = () => {
  if (!hasWindow() || window.__finnixTikTokPixelLoaded || !hasMarketingConsent()) {
    return;
  }

  window.__finnixTikTokPixelLoaded = true;

  (function initTikTokPixel(w, d, t) {
    w.TiktokAnalyticsObject = t;
    const ttq = w[t] = w[t] || [];
    ttq.methods = [
      'page',
      'track',
      'identify',
      'instances',
      'debug',
      'on',
      'off',
      'once',
      'ready',
      'alias',
      'group',
      'enableCookie',
      'disableCookie',
      'holdConsent',
      'revokeConsent',
      'grantConsent',
    ];
    ttq.setAndDefer = function setAndDefer(queue, methodName) {
      queue[methodName] = function deferredMethod() {
        queue.push([methodName].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i += 1) {
      ttq.setAndDefer(ttq, ttq.methods[i]);
    }
    ttq.instance = function instance(pixelId) {
      const instanceQueue = (ttq._i && ttq._i[pixelId]) || [];
      for (let n = 0; n < ttq.methods.length; n += 1) {
        ttq.setAndDefer(instanceQueue, ttq.methods[n]);
      }
      return instanceQueue;
    };
    ttq.load = function load(pixelId, options) {
      const scriptUrl = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[pixelId] = [];
      ttq._i[pixelId]._u = scriptUrl;
      ttq._t = ttq._t || {};
      ttq._t[pixelId] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[pixelId] = options || {};

      const script = d.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = `${scriptUrl}?sdkid=${pixelId}&lib=${t}`;
      const firstScript = d.getElementsByTagName('script')[0];
      firstScript.parentNode.insertBefore(script, firstScript);
    };

    ttq.load(TIKTOK_PIXEL_ID);
    if (hasMarketingConsent()) {
      ttq.enableCookie();
      ttq.grantConsent();
    } else {
      ttq.disableCookie();
    }
    window.__finnixTikTokLastPage = getCurrentPath();
    ttq.page();
  }(window, document, 'ttq'));
};

export const loadMarketingTrackers = () => {
  if (!hasWindow() || window.__finnixMarketingTrackersLoaded || !hasMarketingConsent()) {
    return;
  }

  window.__finnixMarketingTrackersLoaded = true;
  initializeTikTokPixel();
  window.ttq?.enableCookie?.();
  window.ttq?.grantConsent?.();

  const apolloScript = document.createElement('script');
  apolloScript.src = `https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache=${Math.random().toString(36).slice(2)}`;
  apolloScript.async = true;
  apolloScript.defer = true;
  apolloScript.onload = () => {
    window.trackingFunctions?.onLoad?.({ appId: APOLLO_APP_ID });
  };
  document.head.appendChild(apolloScript);

  window._linkedin_partner_id = LINKEDIN_PARTNER_ID;
  window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
  window._linkedin_data_partner_ids.push(window._linkedin_partner_id);
  window.lintrk = window.lintrk || function lintrk() {
    window.lintrk.q.push(arguments);
  };
  window.lintrk.q = window.lintrk.q || [];

  const linkedinScript = document.createElement('script');
  linkedinScript.type = 'text/javascript';
  linkedinScript.async = true;
  linkedinScript.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
  document.body.appendChild(linkedinScript);
};

export const trackTikTokPageView = (pagePath = getCurrentPath()) => {
  if (!hasWindow() || !hasMarketingConsent()) return;

  if (!window.__finnixTikTokPixelLoaded) {
    initializeTikTokPixel();
    return;
  }

  if (window.__finnixTikTokLastPage === pagePath || typeof window.ttq?.page !== 'function') {
    return;
  }

  window.__finnixTikTokLastPage = pagePath;
  window.ttq.page();
};

export const trackTikTokEvent = (eventName, properties = {}) => {
  if (!hasWindow() || !hasMarketingConsent()) return;

  initializeTikTokPixel();

  if (typeof window.ttq?.track !== 'function') {
    return;
  }

  window.ttq.track(eventName, properties);
};

export const saveTikTokCheckout = (checkout) => {
  if (!hasWindow() || !checkout) return;

  try {
    getStorage('sessionStorage')?.setItem(TIKTOK_CHECKOUT_KEY, JSON.stringify(checkout));
  } catch (_error) {
    // Checkout still works if the browser blocks storage.
  }
};

export const getTikTokCheckout = () => {
  try {
    const saved = getStorage('sessionStorage')?.getItem(TIKTOK_CHECKOUT_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch (_error) {
    return null;
  }
};

export const clearTikTokCheckout = () => {
  try {
    getStorage('sessionStorage')?.removeItem(TIKTOK_CHECKOUT_KEY);
  } catch (_error) {
    // Nothing to clear.
  }
};

export const hasTrackedTikTokPurchase = (sessionId) => {
  if (!sessionId) return false;
  try {
    return getStorage('localStorage')?.getItem(`finnix_tiktok_purchase:${sessionId}`) === 'true';
  } catch (_error) {
    return false;
  }
};

export const markTikTokPurchaseTracked = (sessionId) => {
  if (!sessionId) return;
  try {
    getStorage('localStorage')?.setItem(`finnix_tiktok_purchase:${sessionId}`, 'true');
  } catch (_error) {
    // Duplicate protection is best-effort only.
  }
};

export const scheduleMarketingTrackers = () => {
  if (!hasWindow() || !hasMarketingConsent()) return;

  const run = () => loadMarketingTrackers();
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 3000 });
    return;
  }

  window.setTimeout(run, 1500);
};
